import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  GET_ALL_PROJECTS,
  GET_BRANCH_NAMES,
  GET_EMPLOYEES,
  GET_ALL_EMPLOYEES,
  GET_SINGLE_PROJECT,
  LIST_PROJECTS_BY_EMPLOYEE,
  OVERVIEW_COST,
  OVERVIEW_LIST,
  GET_LOCATIONS,
  GET_PROJECT_OVERVIEW_COUNTS,
  GET_INCOME_AND_EXPENSE,
  PROJECT_ITEM_SUMMARY,
  GET_UNUSED_ITEMS_BY_PROJECT,
  LIST_ALL_PROJECTS,
  GET_PROJECT_BY_ID,
  LIST_ALL_PROJECTS_PAGINATION,
} from '../graphql/queries/projects.query';
import {
  CREATE_PROJECT,
  CREATE_PROJECT_RESOURCE,
  CREATE_PROJECT_TASK,
  DELET_PROJECT,
  DELET_PROJECT_RESOURCE,
  DELET_PROJECT_TASK,
  EXPORT_PROJECT_CSV,
  EXPORT_PROJECT_PDF,
  REMOVE_PROJECT,
  UPDATE_PROJECT,
  UPDATE_PROJECT_WITH_ID,
} from '../graphql/mutations/projects.mutation';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  constructor(private apollo: Apollo) {}
  getEmployees(search: string, filter: {}): Observable<any> {
    return this.apollo
      .use('hrms')
      .query<any>({
        query: GET_EMPLOYEES,
        variables: {
          search,
          filter,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data?.getEmployees || []));
  }

  getAllEmployees(search: string, departmentId?: number): Observable<any> {
    return this.apollo
      .use('hrms')
      .query<any>({
        query: GET_ALL_EMPLOYEES,
        variables: {
          search,
          departmentId
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data?.getAllEmployees || []));
  }

  createProject(
    createProjectModuleInput: any,
    projectTasks: any,
    projectResources: any
  ): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: CREATE_PROJECT,
        variables: {
          createProjectModuleInput: createProjectModuleInput,
          projectTasks: projectTasks,
          projectResources: projectResources,
        },
      })
      .pipe(map((result: any) => result.data.createProjectModel || []));
  }

  deleteProject(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: DELET_PROJECT,
        variables: {
          id,
        },
      })
      .pipe(map((result: any) => result.data.deleteProject || []));
  }
  exportProjectsToPDF(input: any, fields: any[]): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: EXPORT_PROJECT_PDF,
        variables: {
          input,
          fields
        },
      })
      .pipe(map((result: any) => result.data.exportProjectsToPDF || []));
  }
  exportProjectsToCSV(input: any, fields: any[]): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: EXPORT_PROJECT_CSV,
        variables: {
          input,
          fields
        },
      })
      .pipe(map((result: any) => result.data.exportProjectsToCSV || []));
  }

  getBranches(): Observable<any[]> {
    return this.apollo
      .use('hrms')
      .watchQuery<any>({ query: GET_BRANCH_NAMES, fetchPolicy: 'network-only' })
      .valueChanges.pipe(map((result) => result?.data?.branches || []));
  }

  fetchLocations(search: string = ''): Observable<any> {
    return this.apollo
      .use('hrms')
      .watchQuery<any>({
        query: GET_LOCATIONS,
        variables: { search },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.locations || []));
  }

  getAllProjects(search: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: GET_ALL_PROJECTS,
        variables: { search },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.listProjects || []));
  }

  findAllProjectsWithPagination(
    input: any,
  ): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: LIST_ALL_PROJECTS_PAGINATION,
        variables: {
          input
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.findAllProjectsWithPagination || [])
      );
  }

  listAllProjectsByEmployee(id: number): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: LIST_PROJECTS_BY_EMPLOYEE,
        variables: {
          id,
        },
        fetchPolicy: 'no-cache',
      })
      .valueChanges.pipe(
        map((result) => result.data?.getProjectsByEmployee || [])
      );
  }

  getOneProject(id: number): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: GET_SINGLE_PROJECT,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.projectDetail || []));
  }

  getProjectOverviewCounts(projectId: number): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_PROJECT_OVERVIEW_COUNTS,
        variables: {
          projectId,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.getProjectOverviewCounts || [])
      );
  }

  projectOverviewCost(id: number): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: OVERVIEW_COST,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.projectOverviewCost || [])
      );
  }

  projectOverviewList(id: number): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: OVERVIEW_LIST,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.projectOverviewList || [])
      );
  }

  updateProject(
    updateProjectModuleInput: any,
    projectTasks: any,
    projectResources: any
  ): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: UPDATE_PROJECT,
        variables: {
          updateProjectModuleInput: updateProjectModuleInput,
          projectTasks: projectTasks,
          projectResources: projectResources,
        },
      })
      .pipe(map((result: any) => result.data.editProject || []));
  }
  createProjectResource(
    createProjectResourceModuleInput: any
  ): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: CREATE_PROJECT_RESOURCE,
        variables: {
          createProjectResourceModuleInput: createProjectResourceModuleInput,
        },
      })
      .pipe(
        map((result: any) => result.data.createProjectResourceModuleInput || [])
      );
  }

  createProjectTasks(createProjectTaskModuleInput: any): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: CREATE_PROJECT_TASK,
        variables: {
          createProjectTaskModuleInput: createProjectTaskModuleInput,
        },
      })
      .pipe(
        map((result: any) => result.data.createProjectTaskModuleInput || [])
      );
  }
  deleteProjectResource(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: DELET_PROJECT_RESOURCE,
        variables: {
          id,
        },
      })
      .pipe(map((result: any) => result.data.deleteProjectResource || []));
  }
  deleteProjectTask(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: DELET_PROJECT_TASK,
        variables: {
          id,
        },
      })
      .pipe(map((result: any) => result.data.deleteProjectTask || []));
  }

  getIncomeAndExpense(projectId: number, year: number): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_INCOME_AND_EXPENSE,
        variables: { projectId, year },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => {
          return result?.data?.getIncomeAndExpense || [];
        })
      );
  }

  listProjectItemSummary(projectId: any) {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: PROJECT_ITEM_SUMMARY,
        fetchPolicy: 'no-cache',
        variables: {
          projectId,
        },
      })
      .valueChanges.pipe(
        map((response) => response.data?.projectItemSummaryById)
      );
  }

  listUnusedItemsByProject(id: any) {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: GET_UNUSED_ITEMS_BY_PROJECT,
        fetchPolicy: 'no-cache',
        variables: {
          id,
        },
      })
      .valueChanges.pipe(
        map((response) => response.data?.getUnusedItemsByProject)
      );
  }

  listAllProjects(search: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: LIST_ALL_PROJECTS,
        variables: {
          search,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.projects || []));
  }

  removeProject(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: REMOVE_PROJECT,
        variables: {
          id,
        },
      })
      .pipe(map((result: any) => result.data.removeProject || []));
  }

  getProjectById(id: number): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: GET_PROJECT_BY_ID,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.project || []));
  }

  updateProjectWithId(updateProjectInput: any, id: any): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: UPDATE_PROJECT_WITH_ID,
        variables: {
          updateProjectInput: updateProjectInput,
          id: id,
        },
      })
      .pipe(map((result: any) => result.data.updateProject || []));
  }
}
